import Avatar from "@/components/Avatar/Avatar";
import M360MarkdownLink from "@/lib/M360MarkdownLink";
import Icon from "@/components/Icon";
import { normalizeM360ImageSrc } from "@/lib/m360Paths";
import { normalizeChatDisplayText } from "../../normalizeChatDisplayText";
import {
  buildRecordRowDisplay,
  entityKindEyebrow,
  entityKindIcon,
  inferRecordEntityKind,
  type RecordListItemFields
} from "../cardDisplayUtils";
import { AgentLinkChip, AgentNavRow } from "./shared";
import { CardFacilityLocationLine } from "./CardFacilityLocationLine";

export function RecordPreviewRow({ item }: { item: RecordListItemFields }) {
  const kind = inferRecordEntityKind(item.href, item.kind);
  const { title, subtitle, meta, showPgaId, pgaId, facility } = buildRecordRowDisplay(item);
  const displayTitle = normalizeChatDisplayText(title);
  const displaySubtitle = subtitle ? normalizeChatDisplayText(subtitle) : undefined;
  const displayMeta = meta ? normalizeChatDisplayText(meta) : undefined;
  const eyebrow = entityKindEyebrow(kind);
  const safeImageUrl = normalizeM360ImageSrc(item.imageUrl);
  const showAvatar = kind === "person" && safeImageUrl;
  const footerLinks = item.links?.filter((link) => !facility.facilityHref || link.href !== facility.facilityHref);
  const hasFooterLinks = Boolean(footerLinks?.length);
  const cardListItem = Boolean((item.href && facility.facilityHref) || (hasFooterLinks && kind === "person"));

  const rowBody = (
    <>
      {showAvatar ? (
        <Avatar picture={safeImageUrl} size="medium" className="shrink-0" />
      ) : (
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sys-brand-secondary-container text-sys-brand-on-secondary-container"
          aria-hidden
        >
          <Icon name={entityKindIcon(kind)} size={20} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        {eyebrow ? <p className="text-label-small text-sys-brand-primary">{eyebrow}</p> : null}
        {cardListItem && item.href ? (
          <p className="text-body-medium font-medium text-sys-brand-on-surface">
            <M360MarkdownLink href={item.href} className="no-underline hover:text-sys-brand-primary">
              {displayTitle}
            </M360MarkdownLink>
          </p>
        ) : (
          <p className="text-body-medium font-medium text-sys-brand-on-surface">{displayTitle}</p>
        )}
        {showPgaId && pgaId ? (
          <p className="font-mono text-body-small text-sys-brand-on-surface-variant">PGA ID {pgaId}</p>
        ) : null}
        {facility.facilityHref || facility.facilityName || facility.location || facility.fallbackSubtitle ? (
          <CardFacilityLocationLine {...facility} />
        ) : displaySubtitle ? (
          <p className="text-body-small text-sys-brand-on-surface-variant">{displaySubtitle}</p>
        ) : null}
        {displayMeta ? <p className="mt-0.5 text-body-small text-sys-brand-on-surface">{displayMeta}</p> : null}
      </div>
      {item.href && !cardListItem ? (
        <Icon name="chevron_right" size={20} className="shrink-0 text-sys-brand-on-surface-variant" aria-hidden />
      ) : null}
    </>
  );

  return (
    <li
      className={
        cardListItem
          ? "overflow-hidden rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest shadow-web-1"
          : undefined
      }
    >
      {cardListItem ? (
        <div className="flex w-full items-center gap-3 p-3">{rowBody}</div>
      ) : (
        <AgentNavRow href={item.href}>{rowBody}</AgentNavRow>
      )}
      {hasFooterLinks ? (
        <div className="flex flex-wrap gap-2 border-t border-sys-brand-outline-variant/70 px-3 py-2">
          {footerLinks?.map((link) => (
            <AgentLinkChip key={`${link.href}-${link.label}`} {...link} />
          ))}
        </div>
      ) : null}
    </li>
  );
}
